// Column defs for the Invoicing tab's two views. Same shape as
// Generateinvoicetable.constants.js ({ key, label, sortable, searchable })
// so both feed straight into BaseDataTable / InvoiceViewAccordionTable the
// same way the Generate Invoices tables do.

const BILLING_TYPE_OPTIONS = [
  { text: 'Market', value: 'Market' },
  { text: 'LIMOSS', value: 'LIMOSS' },
]

// Invoice view Status column (LIQ-2073 payment state). Archived only
// appears as an option for Superusers — the status filter is their only
// way to see archived invoices, and the proxy strips it for anyone else.
export function getInvoiceStatusOptions(isSuperuser = false) {
  const options = [
    { text: 'Unpaid', value: 'Unpaid' },
    { text: 'Partially Paid', value: 'Partially Paid' },
    { text: 'Paid', value: 'Paid' },
  ]
  if (isSuperuser) options.push({ text: 'Archived', value: 'Archived' })
  return options
}

// The selection checkbox column both views put first (LIQ-2071).
export const SELECT_COLUMN = { key: 'select', label: '', sortable: false, searchable: false }

export function getInvoiceViewColumns({ isSuperuser = false } = {}) {
  return [
    { key: 'invoice_ref', label: 'Invoice No.', sortable: true, searchable: true },
    { key: 'amount', label: 'Invoice Amount', sortable: true, searchable: true },
    // Payments / Remaining Balance (LIQ-2073/2076) — summed from recorded
    // payments; filtered as text, like Invoice Amount.
    { key: 'amount_paid', label: 'Payments', sortable: true, searchable: true },
    { key: 'remaining_balance', label: 'Remaining Balance', sortable: true, searchable: true },
    { key: 'invoice_status', label: 'Status', sortable: true, searchable: true, options: getInvoiceStatusOptions(isSuperuser) },
    { key: 'managing_agent', label: 'Lead Underwriter', sortable: true, searchable: true },
    // tpa_name isn't a Lincoln field (Company/name lives in LimeIQ's own
    // DB) — real server-side sort/filter needs it resolved to
    // tpa_company_id at the proxy layer (see InvoiceApprovedListProxyView
    // and InvoiceViewTable.vue's FILTER_PARAM_MAP).
    { key: 'tpa_name', label: 'TPA', sortable: true, searchable: true },
    // Submission Period: a single month/year, not a range — see
    // BaseDataTableSearchPopover.vue's datePicker.type passthrough.
    { key: 'submission_period', label: 'Submission Period', sortable: true, searchable: true, datePicker: { type: 'month' } },
    { key: 'invoice_date', label: 'Invoice Date', sortable: true, searchable: true, datePicker: { range: true } },
    { key: 'currency_type', label: 'CCY', sortable: true, searchable: true },
    // Due Date = dispatched_at + 60 days (see InvoiceViewTable.vue) — a
    // day-range picker still filters correctly since the underlying
    // range gets shifted back by the same 60 days server-side.
    { key: 'due_date', label: 'Due Date', sortable: true, searchable: true, datePicker: { range: true } },
    // LIQ-2466
    { key: 'billing_type', label: 'Billing Type', sortable: true, searchable: true, options: BILLING_TYPE_OPTIONS },
    { key: 'syndicate_number', label: 'Syndicate Number', sortable: true, searchable: true },
    // Per-row actions (LIQ-2072) — the same document/resend/archive
    // actions as the toolbar, for one invoice. Add Payment is toolbar-only
    // (LIQ-2077).
    { key: 'row_actions', label: 'Actions', sortable: false, searchable: false },
  ]
}

// Itemized view (flat) — same charge-level fields the accordion sub-table
// shows, plus Managing Agent (dropped from the accordion sub-table since
// it's already shown on the parent invoice row there).
export function getItemizedViewColumns() {
  return [
    { key: 'bdx_key', label: 'LimeIQ Key', sortable: true, searchable: true },
    { key: 'invoice_reference', label: 'Invoice No.', sortable: true, searchable: true },
    { key: 'reporting_period', label: 'Report Period', sortable: true, searchable: true, datePicker: { range: true } },
    { key: 'managing_agent', label: 'Lead Underwriter', sortable: true, searchable: true },
    // tpa_name isn't a Lincoln field — see getInvoiceViewColumns' own
    // comment above; same resolved-at-the-proxy-layer treatment applies
    // here (InvoiceChargesFlatListProxyView).
    { key: 'tpa_name', label: 'TPA', sortable: true, searchable: true },
    // broker has no FK on InvoiceCharge/Invoice at all — Lincoln resolves
    // it by matching the charge's own bdx_key against GlobalUMR, then
    // following GlobalUMR's own broker relation (see
    // InvoiceChargeFlatListView.CONTRIBUTION_CTE_SQL) — already a plain
    // name string by the time it gets here, unlike tpa_name above, so no
    // proxy-layer resolution/translation is needed for this one.
    { key: 'broker', label: 'Broker', sortable: true, searchable: true },
    // LIQ-2466 — billing_type is the charge's own; syndicate_number comes
    // from its invoice (see Lincoln's InvoiceChargeFlatListView).
    { key: 'billing_type', label: 'Billing Type', sortable: true, searchable: true, options: BILLING_TYPE_OPTIONS },
    { key: 'syndicate_number', label: 'Syndicate Number', sortable: true, searchable: true },
    { key: 'charge_description', label: 'Description', sortable: true, searchable: true },
    { key: 'currency_type', label: 'CCY', sortable: true, searchable: true },
    { key: 'amount', label: 'Amount', sortable: true, searchable: true },
    // LIQ-2465 — a TPA ticks this per key; everyone else sees it read-only.
    {
      key: 'received', label: 'Received', sortable: true, searchable: true,
      options: [{ text: 'Yes', value: 'yes' }, { text: 'No', value: 'no' }],
    },
  ]
}

// Accordion sub-table (Invoice view, expanded row) — no Lead Underwriter
// (already on the parent row). Received is the same per-key flag as the
// Itemized view's column (LIQ-2465); Amount stays last so the subtotal
// row lines up under it.
//
// options: renders that column's filter as a dropdown instead of a text box
// (see InvoiceViewTable.vue's accordion filter row).
export function getAccordionChargeColumns() {
  return [
    { key: 'bdx_key', label: 'LimeIQ Key' },
    { key: 'invoice_reference', label: 'Invoice No.' },
    { key: 'reporting_period', label: 'Report Period' },
    { key: 'broker', label: 'Broker' },
    { key: 'billing_type', label: 'Billing Type', options: BILLING_TYPE_OPTIONS },
    { key: 'syndicate_number', label: 'Syndicate Number' },
    { key: 'charge_description', label: 'Description' },
    { key: 'received', label: 'Received', options: [{ text: 'Yes', value: 'yes' }, { text: 'No', value: 'no' }] },
    { key: 'currency_type', label: 'CCY' },
    { key: 'amount', label: 'Amount' },
  ]
}
